import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { LOYALTY_ENABLED } from "@/lib/features";
import { useOrderingStores, usePickupSlots } from "@/lib/ordering";
import { usePaymentsConfig, useSquareCard } from "@/lib/square";
import { useStoreMenu, useUnavailableLines } from "@/lib/storeMenu";
import { ContactSection, PickupSection, TipSection, PaymentSection, OrderSummary, Confirmation, tomorrowInTimezone, validateContact } from "@/components/checkout/Sections";
import { AllergyNotice } from "@/components/AllergyNotice";

const TAX_RATE = 0.08;
const CARD_CONTAINER = "square-card";

export default function Checkout() {
  const { items, subtotal, clear, removeItem, storeId: cartStoreId, setStoreId: setCartStoreId } = useCart();
  const { user, setUser } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const { stores } = useOrderingStores();
  const [storeId, setStoreId] = useState(params.get("store") || cartStoreId || "");
  const config = usePaymentsConfig(storeId);
  const [form, setForm] = useState({ name: user?.name || "", phone: user?.phone || "", email: user?.email || "" });
  const [pickupTime, setPickupTime] = useState("ASAP");
  const [pickupDate, setPickupDate] = useState("");
  const [notes, setNotes] = useState("");
  const [tip, setTip] = useState(0);
  const [contactErrors, setContactErrors] = useState({});
  useEffect(() => { setPendingOrder(null); }, [tip, pickupTime, pickupDate, notes, items]);
  const [busy, setBusy] = useState(false);
  const [placed, setPlaced] = useState(null);
  const [pendingOrder, setPendingOrder] = useState(null);
  const [loyalty, setLoyalty] = useState(null);
  const redeemPoints = 0; // loyalty redemption is feature-flagged off (LOYALTY_ENABLED)

  useEffect(() => { if (stores.length && !stores.find((s) => s.id === storeId)) setStoreId(stores[0].id); }, [stores, storeId]);
  useEffect(() => { if (LOYALTY_ENABLED && user) api.get("/loyalty/me").then((r) => setLoyalty(r.data)); }, [user]);
  const { ready: cardReady, error: cardError, tokenize } = useSquareCard(config, CARD_CONTAINER, !!config?.enabled);

  const store = useMemo(() => stores.find((s) => s.id === storeId), [stores, storeId]);
  useEffect(() => { if (storeId && storeId !== cartStoreId) setCartStoreId(storeId); }, [storeId, cartStoreId, setCartStoreId]);
  const { items: storeMenu, loading: menuLoading } = useStoreMenu(storeId);
  const unavailable = useUnavailableLines(items, storeMenu, menuLoading);
  const requiresAdvanceDate = items.some((item) => !!item.platter);
  const slots = usePickupSlots(store, requiresAdvanceDate);
  useEffect(() => {
    if (requiresAdvanceDate && pickupTime === "ASAP" && slots.length > 0) {
      setPickupTime(slots[0].value);
    }
  }, [pickupTime, requiresAdvanceDate, slots]);
  const discount = LOYALTY_ENABLED && loyalty ? (redeemPoints / 500) * 5 : 0;
  const discounted = Math.max(0, subtotal - discount);
  const tax = Math.round(discounted * TAX_RATE * 100) / 100;
  const total = Math.round((discounted + tax + tip) * 100) / 100;

  const createOrder = async () => {
    if (pendingOrder) return pendingOrder;
    const { data } = await api.post("/orders", {
      items: items.map(({ item_id, line_id, name, price, quantity, platter }) => ({
        item_id,
        cart_line_id: line_id,
        name,
        price,
        quantity,
        platter,
      })),
      fulfillment: "pickup", location: store.name, store_id: store.id, notes,
      payment_method: "card", pickup_time: pickupTime, pickup_date: pickupDate, tip,
      customer_name: form.name, customer_phone: form.phone, customer_email: form.email,
      points_to_redeem: redeemPoints,
    });
    setPendingOrder(data);
    return data;
  };

  const finish = async (order) => {
    setPlaced(order); setPendingOrder(null); clear();
    if (user) { try { const me = await api.get("/auth/me"); setUser(me.data); } catch { /* ignore */ } }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!items.length) return toast.error("Your cart is empty");
    if (!store) return toast.error("Choose a pickup location");
    if (unavailable.length) {
      return toast.error(`${unavailable.map((l) => l.name).join(", ")} ${unavailable.length > 1 ? "are" : "is"} unavailable at ${store.name}. Remove ${unavailable.length > 1 ? "them" : "it"} or pick another location.`);
    }
    if (requiresAdvanceDate && !pickupDate) {
      return toast.error("Choose a pickup date at least one day ahead for party platters");
    }
    if (requiresAdvanceDate && pickupDate < tomorrowInTimezone(store?.timezone)) {
      return toast.error("Party platter pickup must be at least one day ahead");
    }
    const check = validateContact(form);
    setContactErrors(check.errors);
    if (!check.valid) {
      toast.error("Please check your contact info — phone must be 10 digits and email must be valid");
      return;
    }
    if (!config?.enabled) return toast.error("Online payments are temporarily unavailable");
    if (!cardReady) return toast.error("Card form is still loading");
    setBusy(true);
    try {
      // Create the order first so the server-priced total is the single source of truth for the charge.
      const order = await createOrder();
      if (Math.abs(order.total - total) > 0.005) {
        toast.info(`Menu prices changed — your total is now $${order.total.toFixed(2)}.`);
      }
      const { sourceId, verificationToken } = await tokenize({ amount: order.total, ...form });
      const { data } = await api.post("/payments/square", { order_id: order.id, source_id: sourceId, verification_token: verificationToken });
      await finish({ ...order, payment_status: "paid", card_brand: data.card_brand, card_last4: data.card_last4 });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  if (placed) return <Confirmation placed={placed} store={store} user={user} onMore={() => nav("/menu")} onOrders={() => nav("/account")}/>;

  const label = `Pay $${total.toFixed(2)} & Place Order`;
  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-10 py-12 lg:py-16">
      <p className="eyebrow mb-2">Pickup order</p>
      <h1 className="font-display font-bold text-4xl tracking-tight mb-8">Checkout</h1>
      <form onSubmit={submit} className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          <ContactSection form={form} setForm={setForm} errors={contactErrors}/>
          <PickupSection stores={stores} storeId={storeId} setStoreId={setStoreId} slots={slots} pickupTime={pickupTime} setPickupTime={setPickupTime} pickupDate={pickupDate} setPickupDate={setPickupDate} requiresAdvanceDate={requiresAdvanceDate} storeTimezone={store?.timezone} notes={notes} setNotes={setNotes}/>
          <AllergyNotice compact />
          <TipSection subtotal={subtotal} tip={tip} setTip={setTip}/>
          <PaymentSection config={config} cardReady={cardReady} cardError={cardError} containerId={CARD_CONTAINER}/>
        </div>
        <OrderSummary items={items} subtotal={subtotal} discount={discount} tax={tax} tip={tip} total={total} user={user} submitLabel={label}
          unavailable={unavailable} storeName={store?.name} onRemoveUnavailable={(lineId) => removeItem(lineId)}
          disabled={busy || !items.length || !store || !cardReady || unavailable.length > 0} busy={busy}/>
      </form>
    </div>
  );
}
